import { useMemo, useState } from "react";
import { ArrowRight, Save, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const SITE_TYPES = [
  { id: "vitrine", base: 390 },
  { id: "ecommerce", base: 1290 },
  { id: "webapp", base: 2490 },
];

const FEATURES = [
  { id: "auth", price: 150 },
  { id: "paiement", price: 350 },
  { id: "blog", price: 200 },
  { id: "multilingue", price: 200 },
  { id: "seo", price: 150 },
  { id: "dashboard", price: 350 },
  { id: "api", price: 250 },
];

const HOSTING = [
  { id: "none", price: 0 },
  { id: "standard", price: 60 },
  { id: "premium", price: 150 },
];

export const QuoteSimulator = ({ compact = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();

  const fmt = (n) =>
    new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-GB", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);

  const labelType = (id) =>
    id === "vitrine" ? t("sim.type_vitrine") : id === "ecommerce" ? t("sim.type_ecom") : t("sim.type_app");
  const descType = (id) =>
    id === "vitrine" ? t("sim.type_vitrine_d") : id === "ecommerce" ? t("sim.type_ecom_d") : t("sim.type_app_d");

  const labelFeature = (id) =>
    ({
      auth: t("sim.feat_auth"),
      paiement: t("sim.feat_paiement"),
      blog: t("sim.feat_blog"),
      multilingue: t("sim.feat_multi"),
      seo: t("sim.feat_seo"),
      dashboard: t("sim.feat_dash"),
      api: t("sim.feat_api"),
    }[id]);

  const labelHosting = (id) =>
    id === "none" ? t("sim.host_none") : id === "standard" ? t("sim.host_std") : t("sim.host_prem");

  const [siteType, setSiteType] = useState("vitrine");
  const [pages, setPages] = useState(5);
  const [features, setFeatures] = useState([]);
  const [hosting, setHosting] = useState("standard");
  const [domain, setDomain] = useState(true);
  const [loading, setLoading] = useState(false);

  const toggleFeature = (id) =>
    setFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));

  const breakdown = useMemo(() => {
    const base = SITE_TYPES.find((s) => s.id === siteType)?.base || 0;
    const extraPages = Math.max(0, pages - 5) * 80;
    const featuresTotal = features.reduce(
      (sum, id) => sum + (FEATURES.find((f) => f.id === id)?.price || 0),
      0
    );
    const host = HOSTING.find((h) => h.id === hosting)?.price || 0;
    const dom = domain ? 12 : 0;
    return {
      base,
      extraPages,
      features: featuresTotal,
      hosting: host,
      domain: dom,
      total: base + extraPages + featuresTotal + host + dom,
    };
  }, [siteType, pages, features, hosting, domain]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post("/quotes", {
        params: { site_type: siteType, pages, features, hosting, domain },
      });
      toast.success(t("quotes.intro"));
      navigate("/dashboard?tab=quotes");
    } catch {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="simulateur" data-testid="simulator-section" className={compact ? "" : "py-24 sm:py-32 border-b border-white/10"}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        {!compact && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-zinc-500 mb-4">
                {t("sim.label")}
              </p>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter max-w-2xl">
                {t("sim.title_a")}
                <em className="italic text-zinc-400">{t("sim.title_em")}</em>
                {t("sim.title_b")}
              </h2>
            </div>
            <p className="text-sm text-zinc-400 max-w-md">{t("sim.intro")}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          <div className="lg:col-span-8 bg-zinc-950 p-8 sm:p-10 space-y-12">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-5">
                {t("sim.step1")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {SITE_TYPES.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`sim-type-${s.id}`}
                    onClick={() => setSiteType(s.id)}
                    className={`text-left p-5 border transition-colors ${
                      siteType === s.id ? "border-white bg-zinc-900" : "border-white/10 hover:border-white/40"
                    }`}
                  >
                    <p className="font-display text-lg">{labelType(s.id)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{descType(s.id)}</p>
                    <p className="font-mono text-xs text-zinc-400 mt-3">base {fmt(s.base)}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-5">
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500">
                  {t("sim.step2")}
                </p>
                <span className="font-mono text-sm text-zinc-300">
                  {pages} {pages > 1 ? t("sim.pages_unit") : t("sim.pages_unit_one")}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="40"
                value={pages}
                onChange={(e) => setPages(parseInt(e.target.value, 10))}
                data-testid="sim-pages-slider"
                className="w-full accent-white"
              />
              <p className="text-xs text-zinc-500 mt-2">{t("sim.pages_note")}</p>
            </div>

            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-5">
                {t("sim.step3")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FEATURES.map((f) => {
                  const active = features.includes(f.id);
                  return (
                    <button
                      type="button"
                      key={f.id}
                      data-testid={`sim-feature-${f.id}`}
                      onClick={() => toggleFeature(f.id)}
                      className={`flex items-center justify-between p-4 border text-left transition-colors ${
                        active ? "border-white bg-zinc-900" : "border-white/10 hover:border-white/40"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`block w-4 h-4 border ${active ? "bg-white border-white" : "border-white/30"} flex items-center justify-center`}
                        >
                          {active && <Check size={12} className="text-zinc-950" />}
                        </span>
                        <span className="text-sm">{labelFeature(f.id)}</span>
                      </div>
                      <span className="font-mono text-xs text-zinc-500">+{fmt(f.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-5">
                {t("sim.step4")}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {HOSTING.map((h) => (
                  <button
                    type="button"
                    key={h.id}
                    data-testid={`sim-hosting-${h.id}`}
                    onClick={() => setHosting(h.id)}
                    className={`p-5 border text-left transition-colors ${
                      hosting === h.id ? "border-white bg-zinc-900" : "border-white/10 hover:border-white/40"
                    }`}
                  >
                    <p className="font-display text-lg">{labelHosting(h.id)}</p>
                    <p className="font-mono text-xs text-zinc-500 mt-1">
                      {h.price === 0 ? t("sim.host_none") : `${fmt(h.price)}${t("sim.host_yearly")}`}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-5">
                {t("sim.step5")}
              </p>
              <button
                type="button"
                data-testid="sim-domain-toggle"
                onClick={() => setDomain((d) => !d)}
                className={`flex items-center justify-between w-full p-4 border transition-colors ${
                  domain ? "border-white bg-zinc-900" : "border-white/10 hover:border-white/40"
                }`}
              >
                <span className="text-sm">{t("sim.domain_label")}</span>
                <span className="flex items-center gap-3">
                  <span className="font-mono text-xs text-zinc-500">+12€{t("sim.host_yearly")}</span>
                  <span className={`block w-10 h-5 relative transition-colors ${domain ? "bg-white" : "bg-zinc-700"}`}>
                    <span
                      className={`block w-4 h-4 bg-zinc-950 absolute top-0.5 transition-all ${
                        domain ? "left-[22px]" : "left-0.5"
                      }`}
                    />
                  </span>
                </span>
              </button>
            </div>
          </div>

          <aside data-testid="sim-summary" className="lg:col-span-4 bg-zinc-900/50 p-8 sm:p-10 sticky top-20 self-start">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-6">{t("sim.summary")}</p>
            <div className="space-y-3 text-sm pb-6 border-b border-white/10">
              <Row label={`${t("sim.row_base")} ${labelType(siteType)}`} value={fmt(breakdown.base)} />
              {breakdown.extraPages > 0 && <Row label={`${t("sim.row_extra")} (${pages - 5})`} value={fmt(breakdown.extraPages)} />}
              <Row label={t("sim.row_features")} value={fmt(breakdown.features)} />
              <Row label={t("sim.row_host")} value={fmt(breakdown.hosting)} />
              <Row label={t("sim.row_domain")} value={fmt(breakdown.domain)} />
            </div>
            <div className="pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("sim.summary_total")}</p>
              <p data-testid="sim-total" className="font-display text-5xl tracking-tighter mt-2">
                {fmt(breakdown.total)}
              </p>
              <p className="text-xs text-zinc-500 mt-2">{t("sim.summary_note")}</p>
            </div>

            <div className="mt-8 space-y-3">
              {user ? (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  data-testid="sim-save-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-6 py-3.5 text-sm font-medium disabled:opacity-50"
                >
                  <Save size={14} />
                  {loading ? t("sim.saving") : t("sim.save_btn")}
                </button>
              ) : (
                <Link
                  to="/register"
                  data-testid="sim-signup-cta"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-6 py-3.5 text-sm font-medium"
                >
                  {t("sim.signup_cta")}
                  <ArrowRight size={14} />
                </Link>
              )}
              <a
                href="#contact"
                data-testid="sim-contact-cta"
                className="w-full inline-flex items-center justify-center border border-white/20 hover:border-white/60 transition-colors px-6 py-3.5 text-sm"
              >
                {t("sim.contact_cta")}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-400">{label}</span>
    <span className="font-mono text-zinc-100">{value}</span>
  </div>
);
